import create from 'zustand'
import store from '../store'
import { State } from '../store/types'
import useTimeout from '../internal/utils/useTimeout'
import React, { useEffect, useCallback } from 'react'
import isReactNative from '../internal/utils/isReactNative'
import { QueryClient, QueryClientProvider } from 'react-query'
import useDiscoverNetwork from '../internal/useDiscoverNetwork'

type Props = {
  target: 'staging' | 'production'
  children: React.ReactNode
  networkId?: number
  networkSlug?: string
}

const queryClient = new QueryClient()

const BeAPI: React.FC<Props> = ({
  target,
  networkId,
  networkSlug,
  children
}) => {
  const stagingLog = (log) => {
    if (target === 'staging') {
      console.log(log)
    }
  }

  useDiscoverNetwork()

  const useStore = create<State>(store)

  const state = useStore(
    useCallback(
      (state) => {
        return {
          networkId: state.networkId,
          target: state.target,
          networkSlug: state.networkSlug,
          setNetworkId: state.setNetworkId,
          setTarget: state.setTarget,
          setNetworkSlug: state.setNetworkSlug,
          setTokens: state.setTokens
        }
      },
      [target, networkId, networkSlug]
    )
  )

  const forceRefresh = () => {
    if (!!isReactNative() && !!target) {
      state.setTarget(target)
    }

    if (!!isReactNative() && !!networkId) {
      state.setNetworkId(networkId)
    }
  }

  /**
   * Force-refresh zustand values on React Native after few MS to activate
   * requests. This is needed to update RQ's observers. It probably will be ok
   * on RN 0.69+, but it'll take a while to land on Expo (~3 to 6 months atm)
   */
  useTimeout(forceRefresh, 200)

  useEffect(() => {
    if (!!target && target !== state.target) {
      state.setTarget(target)

      stagingLog('Starting application in [STAGING] mode')
    }
  }, [target])

  /**
   * If a network ID is passed using props, store it on zustand
   */
  useEffect(() => {
    if (!!networkId && networkId !== state.networkId) {
      queryClient.clear()
      state.setNetworkId(networkId)

      stagingLog(`Network ID: ${networkId}`)
    }
  }, [networkId])

  /**
   * Storing the network slug on zustand
   */
  useEffect(() => {
    if (!!networkSlug && networkSlug !== state.networkSlug) {
      state.setNetworkId(undefined)

      queryClient.clear()
      queryClient.resetQueries()

      stagingLog('New network detected. Cache cleared and queries invalidated')

      state.setNetworkSlug(networkSlug)
      state.setTokens({ token: '', refreshToken: '' })
    }
  }, [networkSlug])

  return (
    <>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </>
  )
}

export default BeAPI
